import { SignIn, SignedIn, SignedOut, RedirectToSignIn, UserButton, useAuth } from "@clerk/clerk-react";
import {
  AlertCircle,
  Archive,
  BarChart3,
  Bell,
  Building2,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Download,
  FileUp,
  FolderKanban,
  Home,
  Inbox,
  LifeBuoy,
  Menu,
  Pencil,
  Plus,
  Settings,
  ShieldCheck,
  Trash2,
  X
} from "lucide-react";
import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { apiFetch, apiUrl, currency, readableStatus } from "./lib/api";
import type {
  ChangeRequest,
  Client,
  FileRecord,
  Invoice,
  MeResponse,
  Notification,
  Project,
  RequestCostReportRow,
  WorkEntry
} from "./lib/types";
import logo from "../../Marketing/src/assets/logo.png";

const navIcons = {
  Dashboard: Home,
  Clients: Building2,
  Projects: FolderKanban,
  Requests: Inbox,
  Reports: BarChart3,
  Invoices: CreditCard,
  Files: Archive,
  Settings: Settings,
  Audit: ShieldCheck
};

type ApiState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
};

function useApi<T>(path: string) {
  const { getToken } = useAuth();
  const [state, setState] = useState<ApiState<T>>({ data: null, loading: true, error: null });

  async function load() {
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const data = await apiFetch<T>(path, getToken);
      setState({ data, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error: error instanceof Error ? error.message : "Request failed" });
    }
  }

  useEffect(() => {
    void load();
  }, [path]);

  return { ...state, reload: load };
}

function Protected({ children }: { children: ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn signInFallbackRedirectUrl="/app" />
      </SignedOut>
    </>
  );
}

function AppShell() {
  const me = useApi<MeResponse>("/me");
  const [navOpen, setNavOpen] = useState(false);
  const [minimumSplashElapsed, setMinimumSplashElapsed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setMinimumSplashElapsed(true), 2500);
    return () => window.clearTimeout(timer);
  }, []);

  if (me.loading || !minimumSplashElapsed) {
    return <FullPageState title="Loading dashboard" />;
  }

  if (me.error) {
    return (
      <main className="center-screen">
        <section className="auth-panel">
          <AlertCircle aria-hidden="true" />
          <h1>Portal access is not ready</h1>
          <p>{me.error}</p>
          <Link className="button primary" to="/accept-invite/start">
            Accept an invite
          </Link>
        </section>
      </main>
    );
  }

  if (!me.data) {
    return null;
  }

  const user = me.data.user;

  return (
    <div className="app-shell">
      <aside className={`sidebar ${navOpen ? "open" : ""}`}>
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true">
            <img src={logo} alt="" />
          </span>
          <div>
            <strong>Arctic Aura Designs</strong>
            <span>Client Dashboard</span>
          </div>
        </div>
        <nav aria-label="Portal navigation">
          {me.data.navigation.map((item) => {
            const Icon = navIcons[item.label as keyof typeof navIcons] ?? ChevronRight;
            return (
              <NavLink key={item.href} to={item.href} end={item.href === "/app"} onClick={() => setNavOpen(false)}>
                <Icon size={18} aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <a className="support-link" href="mailto:info@arcticauradesigns.com">
          <LifeBuoy size={18} aria-hidden="true" />
          Support
        </a>
      </aside>
      <div className="shell-main">
        <header className="topbar">
          <button className="icon-button mobile-only" type="button" aria-label="Open menu" onClick={() => setNavOpen(true)}>
            <Menu size={20} aria-hidden="true" />
          </button>
          <div>
            <p className="eyebrow">Client and staff workspace</p>
            <h1>{user.role === "client" ? "Your project portal" : "Operations dashboard"}</h1>
          </div>
          <div className="topbar-actions">
            <span className={`role-pill ${user.role}`}>{readableStatus(user.role)}</span>
            <NotificationsMenu />
            <UserButton afterSignOutUrl="/sign-in" />
          </div>
        </header>
        {navOpen ? (
          <button className="scrim" type="button" aria-label="Close menu" onClick={() => setNavOpen(false)} />
        ) : null}
        <main className="content">
          <Routes>
            <Route path="/" element={<Dashboard me={me.data} />} />
            <Route path="/clients" element={<ClientsPage me={me.data} />} />
            <Route path="/clients/:clientId" element={<ClientDetail me={me.data} />} />
            <Route path="/projects" element={<ProjectsPage me={me.data} />} />
            <Route path="/projects/:projectId" element={<ProjectDetail me={me.data} />} />
            <Route path="/change-requests" element={<ChangeRequestsPage me={me.data} />} />
            <Route path="/change-requests/:id" element={<ChangeRequestDetail me={me.data} />} />
            <Route path="/reports" element={<ReportsPage me={me.data} />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/files" element={<FilesPage me={me.data} />} />
            <Route path="/settings" element={<SettingsPage me={me.data} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function NotificationsMenu() {
  const notifications = useApi<{ notifications: Notification[] }>("/notifications");
  const [open, setOpen] = useState(false);

  return (
    <div className="notifications-menu">
      <button
        className="icon-button"
        type="button"
        aria-label="Notifications"
        aria-expanded={open}
        aria-controls="notifications-popover"
        onClick={() => setOpen((current) => !current)}
      >
        <Bell size={19} aria-hidden="true" />
      </button>
      {open ? (
        <section className="notifications-popover" id="notifications-popover" aria-label="Recent notifications">
          <div className="notifications-header">
            <strong>Notifications</strong>
            <button className="popover-close" type="button" aria-label="Close notifications" onClick={() => setOpen(false)}>
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          {notifications.loading ? <p className="notifications-state">Loading activity…</p> : null}
          {notifications.error ? <p className="notifications-state error-text">{notifications.error}</p> : null}
          {notifications.data?.notifications.length ? (
            <ul className="notifications-list">
              {notifications.data.notifications.map((notification) => (
                <li key={notification.id}>
                  <strong>{readableStatus(notification.action)}</strong>
                  <span>
                    {[notification.client_name, notification.actor_name].filter(Boolean).join(" · ") || "Portal activity"}
                  </span>
                  <time dateTime={notification.created_at}>{new Date(notification.created_at).toLocaleString()}</time>
                </li>
              ))}
            </ul>
          ) : notifications.loading || notifications.error ? null : (
            <p className="notifications-state">No recent notifications.</p>
          )}
        </section>
      ) : null}
    </div>
  );
}

function FullPageState({ title }: { title: string }) {
  return (
    <main className="splash-screen" aria-live="polite" aria-busy="true">
      <div className="splash-aurora" aria-hidden="true">
        <span className="aurora-ribbon ribbon-one" />
        <span className="aurora-ribbon ribbon-two" />
        <span className="aurora-ribbon ribbon-three" />
      </div>
      <div className="splash-stars" aria-hidden="true">
        <span /><span /><span /><span /><span /><span />
      </div>
      <section className="splash-content" role="status">
        <div className="splash-emblem" aria-hidden="true">
          <span className="splash-orbit orbit-one" />
          <span className="splash-orbit orbit-two" />
          <div className="splash-logo"><img src={logo} alt="" /></div>
        </div>
        <div className="splash-wordmark">
          <p>Arctic Aura Designs</p>
          <h1>{title}</h1>
          <span>Preparing your private workspace</span>
        </div>
        <div className="splash-progress" aria-hidden="true"><span /></div>
        <div className="splash-dots" aria-hidden="true"><i /><i /><i /></div>
      </section>
      <p className="splash-footer">Client Dashboard · Secure Portal</p>
    </main>
  );
}

function PageLoading({ title }: { title: string }) {
  return (
    <div className="page-loading" role="status" aria-live="polite">
      <span className="page-loading-spinner" aria-hidden="true" />
      <div><strong>{title}</strong><p>Just a moment…</p></div>
    </div>
  );
}

function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty-state">
      <CheckCircle2 aria-hidden="true" />
      <strong>{title}</strong>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

function Badge({ value }: { value: string }) {
  return <span className={`badge ${value}`}>{readableStatus(value)}</span>;
}

function Panel({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="panel">
      <div className="panel-header">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="modal-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-header">
          <div><p className="eyebrow">Quick create</p><h2>{title}</h2></div>
          <button className="icon-button" type="button" aria-label={`Close ${title}`} onClick={onClose}>
            <X size={19} aria-hidden="true" />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

function Dashboard({ me }: { me: MeResponse }) {
  const dashboard = useApi<{
    role: "staff" | "client";
    counts?: { clients: number; openRequests: number; unpaidInvoices: number; activeProjects: number };
    waitingProjects?: Project[];
    auditEvents?: { id: string; action: string; created_at: string; actor_name?: string }[];
    projects?: Project[];
    requests?: ChangeRequest[];
    invoices?: Invoice[];
    files?: FileRecord[];
  }>("/dashboard");

  if (dashboard.loading) return <PageLoading title="Loading dashboard data" />;
  if (dashboard.error) return <EmptyState title={dashboard.error} />;
  if (!dashboard.data) return null;

  if (me.user.role !== "client") {
    const counts = dashboard.data.counts;
    return (
      <div className="stack">
        <div className="metric-grid">
          <Metric label="Active clients" value={counts?.clients ?? 0} />
          <Metric label="Open requests" value={counts?.openRequests ?? 0} />
          <Metric label="Unpaid invoices" value={counts?.unpaidInvoices ?? 0} />
          <Metric label="Active projects" value={counts?.activeProjects ?? 0} />
        </div>
        <div className="two-column">
          <ProjectTable title="Projects waiting on client" projects={dashboard.data.waitingProjects ?? []} />
          <Panel title="Recent audit activity">
            {(dashboard.data.auditEvents ?? []).length ? (
              <ul className="activity-list">
                {dashboard.data.auditEvents?.map((event) => (
                  <li key={event.id}>
                    <strong>{readableStatus(event.action)}</strong>
                    <span>{event.actor_name ?? "System"} · {new Date(event.created_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="No audit activity yet" />
            )}
          </Panel>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="quick-actions">
        <Link className="button primary" to="/app/change-requests?new=1">
          <Plus size={18} aria-hidden="true" />
          New request
        </Link>
        <a className="button subtle" href="mailto:info@arcticauradesigns.com">
          <LifeBuoy size={18} aria-hidden="true" />
          Contact support
        </a>
      </div>
      <div className="two-column">
        <ProjectTable title="Active projects" projects={dashboard.data.projects ?? []} />
        <RequestTable title="Open change requests" requests={dashboard.data.requests ?? []} />
      </div>
      <InvoiceTable invoices={dashboard.data.invoices ?? []} />
      <FileList files={dashboard.data.files ?? []} />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <section className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </section>
  );
}

function ProjectTable({ title, projects, action }: { title: string; projects: Project[]; action?: ReactNode }) {
  return (
    <Panel title={title} action={action}>
      {projects.length ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th>Client</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id}>
                  <td><Link to={`/app/projects/${project.id}`}>{project.name}</Link></td>
                  <td>{project.client_name ?? "Assigned"}</td>
                  <td><Badge value={project.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No projects to show" />
      )}
    </Panel>
  );
}

function RequestTable({ title, requests, action }: { title: string; requests: ChangeRequest[]; action?: ReactNode }) {
  return (
    <Panel title={title} action={action}>
      {requests.length ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Request</th>
                <th>Priority</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td><Link to={`/app/change-requests/${request.id}`}>{request.title}</Link></td>
                  <td>{readableStatus(request.priority)}</td>
                  <td><Badge value={request.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No open requests" />
      )}
    </Panel>
  );
}

function ClientsPage({ me }: { me: MeResponse }) {
  const { getToken } = useAuth();
  const clients = useApi<{ clients: Client[] }>("/clients");
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  if (me.user.role === "client") {
    return <Navigate to="/app" replace />;
  }

  async function createClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setMessage(null);
    setActionError(null);
    setSubmitting(true);

    try {
      await apiFetch("/clients", getToken, {
        method: "POST",
        body: JSON.stringify(Object.fromEntries(form))
      });
      formElement.reset();
      await clients.reload();
      setCreateOpen(false);
      setMessage("Client created.");
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to create client.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="stack">
      {message ? <p className="success-text action-message">{message}</p> : null}
      <Panel
        title="Clients"
        action={<button className="button primary" type="button" onClick={() => { setActionError(null); setCreateOpen(true); }}><Plus size={17} aria-hidden="true" />New client</button>}
      >
        {clients.loading ? <EmptyState title="Loading clients" /> : null}
        {clients.error ? <EmptyState title={clients.error} /> : null}
        {clients.data?.clients.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Billing</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {clients.data.clients.map((client) => (
                  <tr key={client.id}>
                    <td><Link to={`/app/clients/${client.id}`}>{client.name}</Link></td>
                    <td>{client.billing_email}</td>
                    <td><Badge value={client.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>
      {createOpen ? (
        <Modal title="Create client" onClose={() => setCreateOpen(false)}>
          <form className="form-grid" onSubmit={createClient}>
            <label>Name<input name="name" required minLength={2} autoFocus /></label>
            <label>Billing email<input name="billing_email" type="email" required /></label>
            <div className="form-grid form-grid-split">
              <label>Phone<input name="phone" /></label>
              <label>Stripe customer ID<input name="stripe_customer_id" /></label>
            </div>
            <label>Notes<textarea name="company_notes" rows={4} /></label>
            <div className="form-actions">
              <button className="button primary" type="submit" disabled={submitting}><Plus size={18} aria-hidden="true" />{submitting ? "Creating…" : "Create client"}</button>
              <button className="button subtle" type="button" onClick={() => setCreateOpen(false)}>Cancel</button>
            </div>
            {actionError ? <p className="error-text action-message">{actionError}</p> : null}
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

function ClientDetail({ me }: { me: MeResponse }) {
  const { clientId = "" } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const detail = useApi<{ client: Client; projects: Project[]; members: { id: string; email: string; name: string | null; role: string }[] }>(`/clients/${clientId}`);
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function updateClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActionError(null);
    setMessage(null);

    try {
      const form = new FormData(event.currentTarget);
      await apiFetch(`/clients/${clientId}`, getToken, {
        method: "PATCH",
        body: JSON.stringify(Object.fromEntries(form))
      });
      setMessage("Client updated.");
      await detail.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to update client.");
    }
  }

  async function deleteClient() {
    if (!detail.data || !window.confirm(
      `Delete ${detail.data.client.name}? This permanently removes the client and all of their projects, requests, invoices, files, and memberships.`
    )) {
      return;
    }

    setActionError(null);
    try {
      await apiFetch(`/clients/${clientId}`, getToken, { method: "DELETE" });
      navigate("/app/clients", { replace: true });
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to delete client.");
    }
  }

  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setActionError(null);
    setMessage(null);
    try {
      await apiFetch(`/clients/${clientId}/invites`, getToken, {
        method: "POST",
        body: JSON.stringify(Object.fromEntries(form))
      });
      formElement.reset();
      setMessage("Invite sent.");
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to send invite.");
    }
  }

  if (detail.loading) return <PageLoading title="Loading client" />;
  if (detail.error) return <EmptyState title={detail.error} />;
  if (!detail.data) return null;

  return (
    <div className="stack">
      <section className="page-title">
        <div>
          <p className="eyebrow">Client</p>
          <h2>{detail.data.client.name}</h2>
        </div>
        <Badge value={detail.data.client.status} />
        {me.user.role !== "client" ? (
          <div className="page-actions">
            <button className="button danger" type="button" onClick={() => void deleteClient()}>
              <Trash2 size={17} aria-hidden="true" />Delete client
            </button>
          </div>
        ) : null}
      </section>
      {actionError ? <p className="error-text action-message">{actionError}</p> : null}
      {message ? <p className="success-text action-message">{message}</p> : null}
      {me.user.role !== "client" ? (
        <Panel title="Edit client">
          <form className="form-grid form-grid-split" key={detail.data.client.updated_at} onSubmit={updateClient}>
            <label>Name<input name="name" required minLength={2} defaultValue={detail.data.client.name} /></label>
            <label>Billing email<input name="billing_email" type="email" required defaultValue={detail.data.client.billing_email} /></label>
            <label>Phone<input name="phone" defaultValue={detail.data.client.phone ?? ""} /></label>
            <label>Stripe customer ID<input name="stripe_customer_id" defaultValue={detail.data.client.stripe_customer_id ?? ""} /></label>
            <label>Status
              <select name="status" defaultValue={detail.data.client.status}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <label className="full-width">Notes<textarea name="company_notes" rows={4} defaultValue={detail.data.client.company_notes ?? ""} /></label>
            <div className="form-actions full-width">
              <button className="button primary" type="submit">Save client</button>
            </div>
          </form>
        </Panel>
      ) : null}
      <div className="two-column">
        <ProjectTable title="Client projects" projects={detail.data.projects} />
        <Panel title="Members">
          {detail.data.members.length ? (
            <ul className="activity-list">
              {detail.data.members.map((member) => (
                <li key={member.id}>
                  <strong>{member.name ?? member.email}</strong>
                  <span>{member.email} · {member.role}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="No members yet" />
          )}
        </Panel>
      </div>
      {me.user.role !== "client" ? (
        <Panel title="Invite client user">
          <form className="inline-form" onSubmit={invite}>
            <input name="email" type="email" placeholder="client@example.com" required />
            <select name="role" defaultValue="member">
              <option value="member">Member</option>
              <option value="owner">Owner</option>
            </select>
            <button className="button primary" type="submit">Send invite</button>
          </form>
        </Panel>
      ) : null}
    </div>
  );
}

function ProjectsPage({ me }: { me: MeResponse }) {
  const { getToken } = useAuth();
  const projects = useApi<{ projects: Project[] }>("/projects");
  const clients = me.user.role === "client" ? null : useApi<{ clients: Client[] }>("/clients");
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  async function createProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setMessage(null);
    setActionError(null);
    setSubmitting(true);
    try {
      await apiFetch("/projects", getToken, {
        method: "POST",
        body: JSON.stringify(Object.fromEntries(form))
      });
      formElement.reset();
      await projects.reload();
      setCreateOpen(false);
      setMessage("Project created.");
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to create project.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="stack">
      {message ? <p className="success-text action-message">{message}</p> : null}
      <ProjectTable
        title="Projects"
        projects={projects.data?.projects ?? []}
        action={me.user.role !== "client" ? <button className="button primary" type="button" onClick={() => { setActionError(null); setCreateOpen(true); }}><Plus size={17} aria-hidden="true" />New project</button> : undefined}
      />
      {me.user.role !== "client" && createOpen ? (
        <Modal title="Create project" onClose={() => setCreateOpen(false)}>
          <form className="form-grid" onSubmit={createProject}>
            <label>Client
              <select name="client_id" required>
                <option value="">Select client</option>
                {clients?.data?.clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
              </select>
            </label>
            <label>Name<input name="name" required minLength={2} autoFocus /></label>
            <label>Status
              <select name="status" defaultValue="planning">
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="waiting_on_client">Waiting on client</option>
                <option value="paused">Paused</option>
                <option value="complete">Complete</option>
              </select>
            </label>
            <label>Summary<textarea name="summary" rows={4} /></label>
            <div className="form-actions">
              <button className="button primary" type="submit" disabled={submitting}>{submitting ? "Creating…" : "Create project"}</button>
              <button className="button subtle" type="button" onClick={() => setCreateOpen(false)}>Cancel</button>
            </div>
            {actionError ? <p className="error-text action-message">{actionError}</p> : null}
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

function ProjectDetail({ me }: { me: MeResponse }) {
  const { projectId } = useParams();
  const projects = useApi<{ projects: Project[] }>("/projects");
  const project = useMemo(() => projects.data?.projects.find((item) => item.id === projectId), [projects.data, projectId]);

  if (projects.loading) return <PageLoading title="Loading project" />;
  if (!project) return <EmptyState title="Project not found or unavailable" />;

  return (
    <div className="stack">
      <section className="page-title">
        <div>
          <p className="eyebrow">{project.client_name}</p>
          <h2>{project.name}</h2>
        </div>
        <Badge value={project.status} />
      </section>
      <Panel title="Summary">
        <p>{project.summary ?? "No project summary has been added yet."}</p>
      </Panel>
      {me.user.role !== "client" ? (
        <ProjectEditor project={project} onSaved={projects.reload} />
      ) : null}
    </div>
  );
}

function ProjectEditor({ project, onSaved }: { project: Project; onSaved: () => Promise<void> }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function updateProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    setActionError(null);

    try {
      const form = new FormData(event.currentTarget);
      await apiFetch(`/projects/${project.id}`, getToken, {
        method: "PATCH",
        body: JSON.stringify(Object.fromEntries(form))
      });
      setMessage("Project updated.");
      await onSaved();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to update project.");
    }
  }

  async function deleteProject() {
    if (!window.confirm(
      `Delete ${project.name}? Requests and files will remain, but they will no longer be attached to this project.`
    )) {
      return;
    }

    setActionError(null);
    try {
      await apiFetch(`/projects/${project.id}`, getToken, { method: "DELETE" });
      navigate("/app/projects", { replace: true });
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to delete project.");
    }
  }

  return (
    <Panel
      title="Edit project"
      action={(
        <button className="button danger mini" type="button" onClick={() => void deleteProject()}>
          <Trash2 size={16} aria-hidden="true" />Delete project
        </button>
      )}
    >
      <form className="form-grid form-grid-split" key={project.updated_at} onSubmit={updateProject}>
        <label>Name<input name="name" required minLength={2} defaultValue={project.name} /></label>
        <label>Status
          <select name="status" defaultValue={project.status}>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="waiting_on_client">Waiting on client</option>
            <option value="paused">Paused</option>
            <option value="complete">Complete</option>
          </select>
        </label>
        <label>Start date<input name="start_date" type="date" defaultValue={project.start_date?.slice(0, 10) ?? ""} /></label>
        <label>Target date<input name="target_date" type="date" defaultValue={project.target_date?.slice(0, 10) ?? ""} /></label>
        <label className="full-width">Summary<textarea name="summary" rows={4} defaultValue={project.summary ?? ""} /></label>
        <div className="form-actions full-width">
          <button className="button primary" type="submit">Save project</button>
          {message ? <p className="success-text">{message}</p> : null}
          {actionError ? <p className="error-text">{actionError}</p> : null}
        </div>
      </form>
    </Panel>
  );
}

function ChangeRequestsPage({ me }: { me: MeResponse }) {
  const { getToken } = useAuth();
  const requests = useApi<{ requests: ChangeRequest[] }>("/change-requests");
  const projects = useApi<{ projects: Project[] }>("/projects");
  const clients = me.user.role === "client" ? null : useApi<{ clients: Client[] }>("/clients");
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const createOpen = searchParams.get("new") === "1";

  function setCreateOpen(open: boolean) {
    const next = new URLSearchParams(searchParams);
    if (open) next.set("new", "1");
    else next.delete("new");
    setSearchParams(next, { replace: true });
  }

  const clientOptions = me.user.role === "client"
    ? me.memberships.map((membership) => ({ id: membership.client_id, name: membership.client_name }))
    : clients?.data?.clients.map((client) => ({ id: client.id, name: client.name })) ?? [];

  async function createRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const body = Object.fromEntries(form);
    if (!body.project_id) delete body.project_id;
    setMessage(null);
    setActionError(null);
    setSubmitting(true);

    try {
      await apiFetch("/change-requests", getToken, {
        method: "POST",
        body: JSON.stringify(body)
      });
      formElement.reset();
      await requests.reload();
      setCreateOpen(false);
      setMessage("Request submitted.");
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to submit request.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="stack">
      {message ? <p className="success-text action-message">{message}</p> : null}
      <RequestTable
        title="Change requests"
        requests={requests.data?.requests ?? []}
        action={<button className="button primary" type="button" onClick={() => { setActionError(null); setCreateOpen(true); }}><Plus size={17} aria-hidden="true" />New request</button>}
      />
      {createOpen ? (
        <Modal title="Submit request" onClose={() => setCreateOpen(false)}>
        <form className="form-grid" onSubmit={createRequest}>
          <label>Client
            <select name="client_id" required>
              <option value="">Select client</option>
              {clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
            </select>
          </label>
          <label>Project
            <select name="project_id">
              <option value="">No project</option>
              {projects.data?.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
            </select>
          </label>
          <label>Title<input name="title" required minLength={3} autoFocus /></label>
          <label>Priority
            <select name="priority" defaultValue="normal">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </label>
          <label>Description<textarea name="description" required minLength={10} rows={5} /></label>
          <div className="form-actions">
            <button className="button primary" type="submit" disabled={submitting}>{submitting ? "Submitting…" : "Submit request"}</button>
            <button className="button subtle" type="button" onClick={() => setCreateOpen(false)}>Cancel</button>
          </div>
          {actionError ? <p className="error-text action-message">{actionError}</p> : null}
        </form>
        </Modal>
      ) : null}
    </div>
  );
}

function ChangeRequestDetail({ me }: { me: MeResponse }) {
  const { id = "" } = useParams();
  const { getToken } = useAuth();
  const requests = useApi<{ requests: ChangeRequest[] }>("/change-requests");
  const request = useMemo(() => requests.data?.requests.find((item) => item.id === id), [requests.data, id]);

  async function updateStatus(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await apiFetch(`/change-requests/${id}/status`, getToken, {
      method: "PATCH",
      body: JSON.stringify(Object.fromEntries(form))
    });
    await requests.reload();
  }

  if (requests.loading) return <PageLoading title="Loading request" />;
  if (!request) return <EmptyState title="Request not found or unavailable" />;

  return (
    <div className="stack">
      <section className="page-title">
        <div>
          <p className="eyebrow">{request.client_name} {request.project_name ? `· ${request.project_name}` : ""}</p>
          <h2>{request.title}</h2>
        </div>
        <Badge value={request.status} />
      </section>
      <Panel title="Request details">
        <p>{request.description}</p>
      </Panel>
      {me.user.role !== "client" ? <RequestWorklog request={request} /> : null}
      {me.user.role !== "client" ? (
        <ChangeRequestEditor request={request} onSaved={requests.reload} />
      ) : null}
      {me.user.role !== "client" ? (
        <Panel title="Staff status">
          <form className="inline-form" onSubmit={updateStatus}>
            <select name="status" defaultValue={request.status}>
              <option value="submitted">Submitted</option>
              <option value="in_review">In review</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
              <option value="scheduled">Scheduled</option>
              <option value="complete">Complete</option>
            </select>
            <button className="button primary" type="submit">Update status</button>
          </form>
        </Panel>
      ) : null}
    </div>
  );
}

function ChangeRequestEditor({ request, onSaved }: { request: ChangeRequest; onSaved: () => Promise<void> }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const projects = useApi<{ projects: Project[] }>("/projects");
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const projectOptions = projects.data?.projects.filter((project) => project.client_id === request.client_id) ?? [];

  async function updateRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    setActionError(null);

    try {
      const form = new FormData(event.currentTarget);
      const body: Record<string, FormDataEntryValue | null> = Object.fromEntries(form);
      body.project_id = body.project_id || null;
      await apiFetch(`/change-requests/${request.id}`, getToken, {
        method: "PATCH",
        body: JSON.stringify(body)
      });
      setMessage("Request updated.");
      await onSaved();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to update request.");
    }
  }

  async function deleteRequest() {
    if (!window.confirm(`Delete “${request.title}”? This permanently removes the request.`)) {
      return;
    }

    setActionError(null);
    try {
      await apiFetch(`/change-requests/${request.id}`, getToken, { method: "DELETE" });
      navigate("/app/change-requests", { replace: true });
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to delete request.");
    }
  }

  return (
    <Panel
      title="Edit request"
      action={(
        <button className="button danger mini" type="button" onClick={() => void deleteRequest()}>
          <Trash2 size={16} aria-hidden="true" />Delete request
        </button>
      )}
    >
      <form className="form-grid form-grid-split" key={request.updated_at} onSubmit={updateRequest}>
        <label>Project
          <select name="project_id" defaultValue={request.project_id ?? ""}>
            <option value="">No project</option>
            {projectOptions.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
          </select>
        </label>
        <label className="full-width">Title<input name="title" required minLength={3} defaultValue={request.title} /></label>
        <label>Priority
          <select name="priority" defaultValue={request.priority}>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </label>
        <label className="full-width">Description<textarea name="description" required minLength={10} rows={5} defaultValue={request.description} /></label>
        <div className="form-actions full-width">
          <button className="button primary" type="submit">Save request</button>
          {message ? <p className="success-text">{message}</p> : null}
          {actionError ? <p className="error-text">{actionError}</p> : null}
        </div>
      </form>
    </Panel>
  );
}

function localDateValue() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function readableDuration(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (!hours) return `${remaining}m`;
  if (!remaining) return `${hours}h`;
  return `${hours}h ${remaining}m`;
}

function RequestWorklog({ request }: { request: ChangeRequest }) {
  const { getToken } = useAuth();
  const worklog = useApi<{ entries: WorkEntry[]; totals: { minutes: number; cost_cents: number } }>(
    `/change-requests/${request.id}/work-entries`
  );
  const [editing, setEditing] = useState<WorkEntry | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function saveEntry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const hours = Number(form.get("hours") ?? 0);
    const cost = Number(form.get("cost") ?? 0);
    const minutes = Math.round(hours * 60);
    const costCents = Math.round(cost * 100);

    setMessage(null);
    setActionError(null);

    if (!Number.isFinite(hours) || !Number.isFinite(cost) || hours < 0 || cost < 0 || (minutes === 0 && costCents === 0)) {
      setActionError("Enter a valid time, cost, or both.");
      return;
    }

    const payload = {
      entry_date: form.get("entry_date"),
      description: form.get("description"),
      category: form.get("category"),
      minutes,
      cost_cents: costCents
    };

    try {
      await apiFetch(
        editing
          ? `/change-requests/${request.id}/work-entries/${editing.id}`
          : `/change-requests/${request.id}/work-entries`,
        getToken,
        {
          method: editing ? "PATCH" : "POST",
          body: JSON.stringify(payload)
        }
      );
      setMessage(editing ? "Work entry updated." : "Work entry added.");
      setEditing(null);
      formElement.reset();
      await worklog.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to save work entry.");
    }
  }

  async function deleteEntry(entry: WorkEntry) {
    if (!window.confirm(`Delete the ${entry.entry_date} work entry?`)) return;

    setMessage(null);
    setActionError(null);
    try {
      await apiFetch(`/change-requests/${request.id}/work-entries/${entry.id}`, getToken, { method: "DELETE" });
      if (editing?.id === entry.id) setEditing(null);
      setMessage("Work entry deleted.");
      await worklog.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to delete work entry.");
    }
  }

  const totals = worklog.data?.totals ?? { minutes: 0, cost_cents: 0 };

  return (
    <Panel
      title="Internal time & costs"
      action={(
        <div className="summary-pills" aria-label="Work log totals">
          <span>{readableDuration(totals.minutes)}</span>
          <span>{currency(totals.cost_cents)}</span>
        </div>
      )}
    >
      <form className="form-grid worklog-form" key={editing?.id ?? "new"} onSubmit={saveEntry}>
        <div className="form-grid form-grid-split full-width">
          <label>Date<input name="entry_date" type="date" required defaultValue={editing?.entry_date ?? localDateValue()} /></label>
          <label>Category
            <select name="category" defaultValue={editing?.category ?? "labor"}>
              <option value="labor">Labor</option>
              <option value="materials">Materials</option>
              <option value="software">Software</option>
              <option value="travel">Travel</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label>Time (hours)<input name="hours" type="number" min="0" max="24" step="0.25" defaultValue={editing ? editing.minutes / 60 : ""} /></label>
          <label>Internal cost ($)<input name="cost" type="number" min="0" max="1000000" step="0.01" defaultValue={editing ? (editing.cost_cents / 100).toFixed(2) : ""} /></label>
          <label className="full-width">Description<input name="description" required minLength={2} maxLength={500} defaultValue={editing?.description ?? ""} /></label>
        </div>
        <div className="form-actions">
          <button className="button primary" type="submit">{editing ? "Update entry" : "Add entry"}</button>
          {editing ? <button className="button subtle" type="button" onClick={() => setEditing(null)}>Cancel</button> : null}
          {message ? <p className="success-text">{message}</p> : null}
          {actionError ? <p className="error-text">{actionError}</p> : null}
        </div>
      </form>
      {worklog.loading ? <EmptyState title="Loading internal work log" /> : null}
      {worklog.error ? <EmptyState title={worklog.error} /> : null}
      {worklog.data?.entries.length ? (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Work</th><th>Time</th><th>Cost</th><th>Logged by</th><th>Actions</th></tr></thead>
            <tbody>
              {worklog.data.entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{new Date(`${entry.entry_date}T12:00:00`).toLocaleDateString()}</td>
                  <td><strong>{readableStatus(entry.category)}</strong><br /><span>{entry.description}</span></td>
                  <td>{readableDuration(entry.minutes)}</td>
                  <td>{currency(entry.cost_cents)}</td>
                  <td>{entry.logged_by_name ?? entry.logged_by_email}</td>
                  <td>
                    <div className="row-actions">
                      <button className="button mini subtle" type="button" onClick={() => setEditing(entry)}><Pencil size={14} aria-hidden="true" />Edit</button>
                      <button className="button mini danger" type="button" onClick={() => void deleteEntry(entry)}><Trash2 size={14} aria-hidden="true" />Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : worklog.loading || worklog.error ? null : (
        <EmptyState title="No internal time or costs yet">Add the first work entry above. Clients cannot see this section.</EmptyState>
      )}
    </Panel>
  );
}

function ReportsPage({ me }: { me: MeResponse }) {
  const [reportPath, setReportPath] = useState("/reports/request-costs");
  const report = useApi<{
    rows: RequestCostReportRow[];
    totals: { minutes: number; cost_cents: number; entries: number };
  }>(reportPath);
  const clients = useApi<{ clients: Client[] }>("/clients");

  if (me.user.role === "client") return <Navigate to="/app" replace />;

  function filterReport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const query = new URLSearchParams();
    for (const key of ["from", "to", "client_id"]) {
      const value = form.get(key);
      if (typeof value === "string" && value) query.set(key, value);
    }
    setReportPath(`/reports/request-costs${query.size ? `?${query}` : ""}`);
  }

  function exportCsv() {
    if (!report.data?.rows.length) return;
    const escape = (value: string | number | null) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const rows = [
      ["Client", "Project", "Request", "Status", "Hours", "Internal Cost", "Entries", "First Entry", "Last Entry"],
      ...report.data.rows.map((row) => [
        row.client_name,
        row.project_name,
        row.request_title,
        readableStatus(row.request_status),
        (row.total_minutes / 60).toFixed(2),
        (row.total_cost_cents / 100).toFixed(2),
        row.entry_count,
        row.first_entry_date,
        row.last_entry_date
      ])
    ];
    const csv = rows.map((row) => row.map(escape).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `request-time-cost-report-${localDateValue()}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const totals = report.data?.totals ?? { minutes: 0, cost_cents: 0, entries: 0 };

  return (
    <div className="stack">
      <section className="page-title">
        <div><p className="eyebrow">Staff only</p><h2>Request time & cost reports</h2></div>
        <div className="page-actions">
          <button className="button subtle" type="button" onClick={exportCsv} disabled={!report.data?.rows.length}>
            <Download size={17} aria-hidden="true" />Export CSV
          </button>
        </div>
      </section>
      <Panel title="Report filters">
        <form className="form-grid report-filters" onSubmit={filterReport}>
          <label>From<input name="from" type="date" /></label>
          <label>To<input name="to" type="date" /></label>
          <label>Client
            <select name="client_id"><option value="">All clients</option>{clients.data?.clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select>
          </label>
          <button className="button primary" type="submit">Run report</button>
        </form>
      </Panel>
      <div className="metric-grid report-metrics">
        <Metric label="Tracked time" value={readableDuration(totals.minutes)} />
        <Metric label="Internal costs" value={currency(totals.cost_cents)} />
        <Metric label="Work entries" value={totals.entries} />
      </div>
      <Panel title="Requests">
        {report.loading ? <EmptyState title="Running report" /> : null}
        {report.error ? <EmptyState title={report.error} /> : null}
        {report.data?.rows.length ? (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Client / project</th><th>Request</th><th>Time</th><th>Internal cost</th><th>Entries</th><th>Date range</th></tr></thead>
              <tbody>
                {report.data.rows.map((row) => (
                  <tr key={row.request_id}>
                    <td>{row.client_name}<br /><span>{row.project_name ?? "No project"}</span></td>
                    <td><Link to={`/app/change-requests/${row.request_id}`}>{row.request_title}</Link><br /><Badge value={row.request_status} /></td>
                    <td>{readableDuration(row.total_minutes)}</td>
                    <td>{currency(row.total_cost_cents)}</td>
                    <td>{row.entry_count}</td>
                    <td>{new Date(`${row.first_entry_date}T12:00:00`).toLocaleDateString()} – {new Date(`${row.last_entry_date}T12:00:00`).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : report.loading || report.error ? null : <EmptyState title="No tracked work matches these filters" />}
      </Panel>
    </div>
  );
}

function InvoicesPage() {
  const invoices = useApi<{ invoices: Invoice[] }>("/invoices");
  return <InvoiceTable invoices={invoices.data?.invoices ?? []} />;
}

function InvoiceTable({ invoices }: { invoices: Invoice[] }) {
  return (
    <Panel title="Invoices">
      {invoices.length ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Client</th>
                <th>Status</th>
                <th>Due</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{invoice.number ?? "Stripe invoice"}<br /><span>{currency(invoice.amount_due, invoice.currency)}</span></td>
                  <td>{invoice.client_name ?? "Client"}</td>
                  <td><Badge value={invoice.status} /></td>
                  <td>{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "No due date"}</td>
                  <td>{invoice.hosted_invoice_url ? <a className="button mini" href={invoice.hosted_invoice_url} target="_blank" rel="noreferrer">Pay invoice</a> : "Unavailable"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No invoices yet">Invoices will appear here after Stripe sends them to the portal.</EmptyState>
      )}
    </Panel>
  );
}

function FilesPage({ me }: { me: MeResponse }) {
  const { getToken } = useAuth();
  const dashboard = useApi<{ files?: FileRecord[] }>("/dashboard");
  const projects = useApi<{ projects: Project[] }>("/projects");
  const clients = me.user.role === "client" ? null : useApi<{ clients: Client[] }>("/clients");
  const [message, setMessage] = useState<string | null>(null);

  const clientOptions = me.user.role === "client"
    ? me.memberships.map((membership) => ({ id: membership.client_id, name: membership.client_name }))
    : clients?.data?.clients.map((client) => ({ id: client.id, name: client.name })) ?? [];

  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const file = form.get("file");

    if (!(file instanceof File)) {
      return;
    }

    const body = {
      client_id: form.get("client_id"),
      project_id: form.get("project_id") || undefined,
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      size_bytes: file.size
    };

    const prepared = await apiFetch<{ uploadUrl: string; headers: Record<string, string> }>("/files/presign", getToken, {
      method: "POST",
      body: JSON.stringify(body)
    });
    const token = await getToken();
    const response = await fetch(prepared.uploadUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": prepared.headers["Content-Type"]
      },
      body: file
    });

    if (!response.ok) throw new Error("Upload failed");
    setMessage("File uploaded.");
    formElement.reset();
    await dashboard.reload();
  }

  return (
    <div className="two-column wide-left">
      <FileList files={dashboard.data?.files ?? []} />
      <Panel title="Upload file">
        <form className="form-grid" onSubmit={upload}>
          <label>Client
            <select name="client_id" required>
              <option value="">Select client</option>
              {clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
            </select>
          </label>
          <label>Project
            <select name="project_id">
              <option value="">No project</option>
              {projects.data?.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
            </select>
          </label>
          <label>File<input name="file" type="file" required /></label>
          <button className="button primary" type="submit"><FileUp size={18} aria-hidden="true" />Upload</button>
          {message ? <p className="success-text">{message}</p> : null}
        </form>
      </Panel>
    </div>
  );
}

function FileList({ files }: { files: FileRecord[] }) {
  const { getToken } = useAuth();

  async function download(file: FileRecord) {
    const token = await getToken();
    const response = await fetch(apiUrl(`/files/${file.id}/download`), {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    });

    if (!response.ok) {
      throw new Error("Download failed");
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = file.filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Panel title="Recent files">
      {files.length ? (
        <ul className="file-list">
          {files.map((file) => (
            <li key={file.id}>
              <div>
                <strong>{file.filename}</strong>
                <span>{file.client_name ?? "Client"} · {(file.size_bytes / 1024).toFixed(1)} KB</span>
              </div>
              <button className="button mini" type="button" onClick={() => void download(file)}>Download</button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="No files yet">Private uploads and project files will appear here.</EmptyState>
      )}
    </Panel>
  );
}

function SettingsPage({ me }: { me: MeResponse }) {
  const audit = me.user.role === "client" ? null : useApi<{ events: { id: string; action: string; created_at: string; actor_name?: string; client_name?: string }[] }>("/audit-events");

  return (
    <div className="stack">
      <Panel title="Account">
        <dl className="details">
          <div><dt>Email</dt><dd>{me.user.email}</dd></div>
          <div><dt>Role</dt><dd>{readableStatus(me.user.role)}</dd></div>
          <div><dt>Status</dt><dd>{me.user.status}</dd></div>
        </dl>
      </Panel>
      {audit ? (
        <Panel title="Audit events">
          <ul className="activity-list">
            {audit.data?.events.map((event) => (
              <li key={event.id}>
                <strong>{readableStatus(event.action)}</strong>
                <span>{event.client_name ?? "No client"} · {event.actor_name ?? "System"} · {new Date(event.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}

function SignInPage() {
  return (
    <main className="center-screen aurora-bg">
      <section className="auth-panel auth-wide">
        <div>
          <p className="eyebrow">Arctic Aura Designs</p>
          <h1>Sign in to your portal</h1>
          <p>Access projects, requests, invoices, and shared files from one private workspace.</p>
        </div>
        <SignIn routing="path" path="/sign-in" signUpUrl="" />
      </section>
    </main>
  );
}

function AcceptInvitePage() {
  const { token = "" } = useParams();
  const { getToken, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: false, message: "", error: "" });

  async function accept() {
    setState({ loading: true, message: "", error: "" });
    try {
      await apiFetch("/invites/accept", getToken, {
        method: "POST",
        body: JSON.stringify({ token })
      });
      setState({ loading: false, message: "Invite accepted.", error: "" });
      navigate("/app");
    } catch (error) {
      setState({ loading: false, message: "", error: error instanceof Error ? error.message : "Invite failed" });
    }
  }

  return (
    <main className="center-screen aurora-bg">
      <section className="auth-panel">
        <p className="eyebrow">Invite only</p>
        <h1>Accept portal invite</h1>
        {!isSignedIn ? (
          <>
            <p>Sign in first, then return here to accept this invite.</p>
            <Link className="button primary" to="/sign-in">Sign in</Link>
          </>
        ) : (
          <>
            <p>This connects your signed-in Clerk account to the invited client workspace.</p>
            <button className="button primary" type="button" onClick={accept} disabled={state.loading}>
              {state.loading ? "Accepting..." : "Accept invite"}
            </button>
          </>
        )}
        {state.error ? <p className="error-text">{state.error}</p> : null}
        {state.message ? <p className="success-text">{state.message}</p> : null}
      </section>
    </main>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route path="/app/*" element={<Protected><AppShell /></Protected>} />
    </Routes>
  );
}
